// src/redux/store.ts
import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';

// Import reducers
import authReducer from './slices/auth.slice';
import productsReducer from './slices/products.slice';
import inventoryReducer from './slices/inventory.slice';
import pageStatesReducer from './slices/pageStates.slice';
import { inventoryApi } from './services/inventoryApi';

// Configure persist for auth
const authPersistConfig = {
  key: 'auth',
  storage,
  // Persist permissions so reload does not flash a false "no access" before fetchProfile.
  whitelist: ['token', 'user', 'isAuthenticated', 'mustChangePassword', 'permissions'],
};

// Configure persist for pageStates
const pageStatesPersistConfig = {
  key: 'pageStates',
  storage,
  whitelist: [], // persistir todos los campos
};

// Combine reducers
const rootReducer = combineReducers({
  auth: persistReducer(authPersistConfig, authReducer),
  products: productsReducer,
  inventory: inventoryReducer,
  pageStates: persistReducer(pageStatesPersistConfig, pageStatesReducer),
  [inventoryApi.reducerPath]: inventoryApi.reducer,
});

// Create store
export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(inventoryApi.middleware),
});

// Create persistor
export const persistor = persistStore(store);

// Setup listeners for RTK Query
setupListeners(store.dispatch);

// Export types
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
